import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchVendorProducts,
  type FetchProductsParams,
} from "../lib/storefront";
import type { PageInfo, PocaConfig, Product, SortKey } from "../types";

type State = {
  products: Product[];
  pageInfo: PageInfo;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
};

type Filters = {
  search: string;
  minPrice: number | null;
  maxPrice: number | null;
  availableOnly: boolean;
  sortKey: SortKey;
  reverse: boolean;
  collectionId: string | null;
  collectionHandle: string | null;
  memberTag: string | null;
  productTags: string[];
};

const INITIAL_PAGE_INFO: PageInfo = { hasNextPage: false, endCursor: null };

export function useVendorProducts(config: PocaConfig, filters: Filters) {
  const [state, setState] = useState<State>({
    products: [],
    pageInfo: INITIAL_PAGE_INFO,
    loading: true,
    loadingMore: false,
    error: null,
  });

  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (mode: "reset" | "append", cursor: string | null) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      const reqId = ++requestIdRef.current;

      setState((s) => ({
        ...s,
        products: mode === "reset" ? [] : s.products,
        loading: mode === "reset",
        loadingMore: mode === "append",
        error: null,
      }));

      const params: FetchProductsParams = {
        search: filters.search,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        availableOnly: filters.availableOnly,
        sortKey: filters.sortKey,
        reverse: filters.reverse,
        first: config.pageSize,
        after: cursor,
        collectionId: filters.collectionId,
        collectionHandle: filters.collectionHandle,
        memberTag: filters.memberTag,
        productTags: filters.productTags,
      };

      console.log("[poca] useVendorProducts load", {
        mode,
        cursor,
        pageSize: config.pageSize,
        filters,
      });

      try {
        const result = await fetchVendorProducts(config, params, ac.signal);
        if (reqId !== requestIdRef.current) return; // 더 최신 요청이 있음

        setState((s) => {
          const products =
            mode === "append"
              ? [...s.products, ...result.products]
              : result.products;
          console.log("[poca] useVendorProducts done", {
            mode,
            received: result.products.length,
            total: products.length,
            pageInfo: result.pageInfo,
          });
          return {
            products,
            pageInfo: result.pageInfo,
            loading: false,
            loadingMore: false,
            error: null,
          };
        });
      } catch (e) {
        if (ac.signal.aborted) return;
        if (reqId !== requestIdRef.current) return;
        console.error("[poca] useVendorProducts failed", e);
        setState((s) => ({
          ...s,
          loading: false,
          loadingMore: false,
          error: e instanceof Error ? e.message : "Failed to load products",
        }));
      }
    },
    [
      config,
      filters.search,
      filters.minPrice,
      filters.maxPrice,
      filters.availableOnly,
      filters.sortKey,
      filters.reverse,
      filters.collectionId,
      filters.collectionHandle,
      filters.memberTag,
      filters.productTags,
    ]
  );

  useEffect(() => {
    load("reset", null);
    return () => abortRef.current?.abort();
  }, [load]);

  const loadMore = useCallback(() => {
    if (!state.pageInfo.hasNextPage || state.loadingMore || state.loading) return;
    load("append", state.pageInfo.endCursor);
  }, [load, state.pageInfo, state.loadingMore, state.loading]);

  return { ...state, loadMore };
}
